// external
import isEmpty from 'lodash.isempty';

// libraries
import { BaseMiddleware } from '@makemydeal/dr-activities-common';
import {
    Middleware as BaseTradeInMiddleware,
    TRADE_MANUAL_ENTRY,
    TRADE_SUMMARY,
    TRADE_VEHICLE_INFO,
    updateTradeInCompletedState,
    updateTradeInCurrentLocation,
    updateTradeInValuation
} from '@makemydeal/dr-activities-tradein';

// store
import type { HostActions, TradeInConfig } from '../types';
import type { Selectors as SciSelectors } from './selectors';
import type { Store } from 'redux';
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import * as tradeInComponentActionCreators from '@makemydeal/dr-dash-store/actions/tradeInComponentActionCreators';
import { CLEAR_ROLE_ASSUMPTION } from '@makemydeal/dr-activities-role-assumption';
import { navigateTo } from '@makemydeal/dr-activities-common';
import { SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { Actions } from './actions';
import type { Selectors } from '@makemydeal/dr-activities-tradein';
import {
    tradeInPayloadHelpers,
    configureTradeIn,
    createTradeInConfigObject,
    getTradeInCompletionStatus,
    isLoanStepRoute
} from '../utils';
import { TRADE_IN_USER_SOURCE } from '../constants';
import { tradeInHistory } from './tradeInHistory';

/**
 * SCI-specific Trade-In Middleware Wrapper
 *
 * This class wraps the base trade-in middleware from @makemydeal/dr-activities-tradein
 * and adds Sales Check-In specific functionality like:
 * - Auto-updating trade-in to backend when completed
 * - Handling SCI-specific actions (REMOVE_TRADE_IN)
 * - Integration with Sales Check-In store state
 *
 * By delegating to the base middleware, we get:
 * - Complete analytics tracking
 * - Relay message system for external analytics
 * - New Relic logging
 * - Lead form integration
 * - P202 redesign support
 * - Comprehensive ICO flow handling
 */
export class MiddlewareManager extends BaseMiddleware {
    private readonly baseTradeInMiddleware: BaseTradeInMiddleware;
    isConfigured = false;

    constructor(
        public selectors: Selectors,
        public sciSelectors: SciSelectors,
        public hostActions: HostActions,
        public config: TradeInConfig,
        public baseRoute?: string
    ) {
        super(selectors);

        // Initialize the base trade-in middleware to handle all common trade-in logic
        // Use the shared config creator to avoid duplication with utils/config.ts
        const configObject = createTradeInConfigObject(config);
        const baseConfig = {
            ...config,
            ...configObject,
            // Override getCurrentFlowName for SCI-specific behavior
            utils: {
                ...configObject.utils,
                getCurrentFlowName: () => 'sales-checkin'
            },
            // Override offerActionCreators for middleware-specific actions
            offerActionCreators: {
                updatedShopper: (shopperInfo: any) => ({ type: 'UPDATED_SHOPPER', payload: shopperInfo }),
                updatedTradeIn: (tradeIn: any) => ({ type: 'UPDATED_TRADE_IN', payload: tradeIn })
            },
            hostActions: hostActions || {}
        };

        this.baseTradeInMiddleware = new BaseTradeInMiddleware(selectors, baseConfig as any, baseRoute);
    }

    private isLoanFlowEnabled(state: any): boolean {
        return this.selectors.additionalSelectors.configSelectors.isAmdLienPayoffQuoteSpOnSP(state);
    }

    /**
     * Auto-update trade-in to backend when completed (SCI-specific)
     */
    private autoUpdateTradeInIfCompleted(store: Store<any>, dealXgVersion: string | undefined): void {
        const state = store.getState();
        const isCompleted = getTradeInCompletionStatus(
            state.tradeInComponent.currentUserLocationRoute,
            state.tradeInComponent.value
        );
        const activeDealDetails = state.salesCheckin?.activeDeal?.details;
        const dealXgId = activeDealDetails?.dealId;

        if (!dealXgId || !isCompleted) {
            return;
        }

        // `isCompleted` above is derived from the current route, but tradeInPayloadHelpers gates the
        // payload on tradeInComponent.isCompleted. In the Submit-for-Appraisal flow with the appraisal
        // screen turned OFF, the base activity dispatches TRADE_IN_SAVE_OR_UPDATE while still on
        // /vehicleInfo and only navigates to the summary afterwards, so the reducer computed that flag
        // from the pre-navigation route and it is still false by the time we get here. Commit the
        // completion decision to state so both agree — otherwise the payload comes back empty and the
        // BFF drops the trade as invalid, saving the deal with no trade-in at all.
        if (!state.tradeInComponent.isCompleted) {
            store.dispatch(updateTradeInCompletedState());
        }
        const completedState = store.getState();

        const tradeInPayload = {
            tradeIn: tradeInPayloadHelpers(completedState, this.selectors, this.isLoanFlowEnabled(completedState)),
            ...this.buildDealChangePayload(
                store,
                dealXgId,
                // Fall back to the version on screen. The BFF treats a missing version as "latest", so
                // omitting it while the user is on an older version (opened from the Deal List) applies
                // the trade to latest instead. activeDealDetails is non-null — the dealXgId guard proved it.
                dealXgVersion ?? activeDealDetails.dealStructure?.version,
                completedState.bridgeUser?.username
            )
        };
        store.dispatch(tradeInComponentActionCreators.updateSCITradeIn(SALES_CHECKIN_APP_PREFIX, tradeInPayload));
    }

    /**
     * Marks the deal as needing manager re-approval and stamps the change type/acceptance reset that
     * every SCI trade-in mutation (add or remove) requires, then assembles the fields shared by both
     * the updateSCITradeIn and removeSCITradeIn payloads. Callers pass dealXgId/dealXgVersion/
     * salesPersonUsername in explicitly since the two call sites source them from different state
     * snapshots (see autoUpdateTradeInIfCompleted vs. handleRemoveTradeIn).
     */
    private buildDealChangePayload(
        store: Store<any>,
        dealXgId: string,
        dealXgVersion: string | undefined,
        salesPersonUsername: string | undefined
    ) {
        const visibility = this.updateDealVisibility(store);
        const acceptedByBuyer = false;
        store.dispatch(this.hostActions.setDealAcceptedByBuyer(acceptedByBuyer));

        return {
            dealXgId,
            dealXgVersion,
            salesPersonUsername,
            acceptedByBuyer,
            ...(visibility && !isEmpty(visibility) ? { visibility } : {}),
            ...this.getDealerAndSalesViewMode(store)
        };
    }

    private updateDealVisibility(store: Store<any>) {
        const state = store.getState();
        const visibility = this.sciSelectors.additionalSelectors.getDealVisibility(state);
        if (visibility && !isEmpty(visibility)) {
            visibility.approvedByManager = false;
            visibility.isDraftDeal = false;
            visibility.needsManagerApproval = true;
            store.dispatch(this.hostActions.updateActiveDealVisibilityStatus(visibility));
        }
        return visibility;
    }

    private getDealerAndSalesViewMode(store: Store<any>): { dealer: { mmdId: string }; salesViewMode: string } {
        const state = store.getState();
        const dealerDetails = this.sciSelectors.additionalSelectors.useDealerDetailsSelector(state);
        return {
            dealer: {
                mmdId: dealerDetails?.dealerMMDId
            },
            salesViewMode: dealerDetails?.dealerSettings?.salesViewMode
        };
    }

    private handleBootstrapSuccess(store: Store<any>, next: any, action: any): void {
        if (!this.isConfigured) {
            const config = (action.meta && action.meta.tradeInConfig) as TradeInConfig;
            configureTradeIn({ config });
            this.isConfigured = true;
        }
        this.baseTradeInMiddleware.middlewareFunction(store, next, action);
    }

    private handleRemoveTradeIn(store: Store<any>, next: any, action: any, state: any): void {
        if (!this.sciSelectors.additionalSelectors.hasTradeIn(state)) {
            // Nothing was ever saved to the deal, so this is cancelling an in-progress entry,
            // not removing a persisted trade-in. Skip the backend call: firing it anyway sends
            // a pointless DELETE that can race a subsequent add (its late response overwrites
            // activeDeal/workflow state from underneath the new attempt) and drives the
            // "Trade-In information removed successfully" toast even though nothing was removed.
            this.baseTradeInMiddleware.middlewareFunction(store, next, action);
            return;
        }

        const dealXgId = state.salesCheckin.activeDeal.details?.dealId || '';
        const tradeInPayload = this.buildDealChangePayload(
            store,
            dealXgId,
            // Same reason as the add path above: a missing version makes the BFF strip the
            // trade from latest rather than from the version the user is looking at.
            state.salesCheckin.activeDeal.details?.dealStructure?.version,
            state.bridgeUser?.username
        );
        store.dispatch(tradeInComponentActionCreators.removeSCITradeIn(SALES_CHECKIN_APP_PREFIX, tradeInPayload));
        // Delegate to base middleware for analytics
        this.baseTradeInMiddleware.middlewareFunction(store, next, action);
    }

    private handleSaveOrUpdate(store: Store<any>, next: any, action: any, state: any): void {
        // Delegate to base middleware first (analytics, relay messages, offer state).
        this.baseTradeInMiddleware.middlewareFunction(store, next, action);
        const wasManualEntry = state.tradeInComponent.currentUserLocationRoute === TRADE_MANUAL_ENTRY;
        // Persist here only for the manual/Submit-for-Appraisal flow (manual-entry route),
        // which has no estimate step. In the estimate flow this fires before the summary
        // re-estimates, so we skip it and let TRADE_IN_ESTIMATION_SUCCESS do the single write.

        // When the dealer's only estimate method is 'Dealer Submit for appraisal' and 'show
        // Dealer Submit Appraisal Screen' is turned OFF, the base activity skips the
        // manual-entry route entirely and dispatches TRADE_IN_SAVE_OR_UPDATE from /vehicleInfo,
        // so `wasManualEntry` never matches. Recognise that flow from the dealer setting plus
        // the Submit-for-Appraisal source. Keyed on the setting rather than on `value === 0`,
        // which also matches a dealer who legitimately appraised at $0 — that case already
        // matches `wasManualEntry`, and matching both would post twice.
        const wasAppraisalScreenSkipped =
            state.tradeInComponent.source === TRADE_IN_USER_SOURCE && !this.selectors.isShowAppraisalValueScreenSalesView(state);

        if (wasAppraisalScreenSkipped) {
            // Skipping the manual-entry page also skips the updateTradeInValuation() that page
            // dispatches on continue — and that is the only thing reaching the base middleware's
            // UPDATE_TRADE_IN, which is what populates tradeInComponent.saved. Without it `saved`
            // keeps its initial empty vehicle. Supply it here so `saved` carries the trade
            // vehicle like it does in every other estimate flow.
            store.dispatch(updateTradeInValuation());
        }

        if (!this.isLoanFlowEnabled(state) && (wasManualEntry || wasAppraisalScreenSkipped)) {
            this.autoUpdateTradeInIfCompleted(store, action.payload?.dealXgVersion);
        }
    }

    private handleLocationChange(store: Store<any>, action: any, state: any): void {
        const { nextRoute } = action.payload as any;
        store.dispatch(navigateTo(`${nextRoute}`));

        if (!this.isLoanFlowEnabled(state)) {
            return;
        }

        if (nextRoute === TRADE_SUMMARY && state.tradeInComponent.loanStepAnswered) {
            this.autoUpdateTradeInIfCompleted(store, undefined);
            store.dispatch({ type: Actions.TRADE_IN_LOAN_FLOW_PERSISTED });
        }
    }

    private handleCompletedValuation(store: Store<any>, next: any, action: any, state: any): void {
        this.baseTradeInMiddleware.middlewareFunction(store, next, action);

        if (!this.isLoanFlowEnabled(state) || !store.getState().tradeInComponent.loanStepPending) {
            this.autoUpdateTradeInIfCompleted(store, action.payload?.dealXgVersion);
        }
    }

    private handleAddIcoValue(store: Store<any>, state: any): void {
        if (!this.isLoanFlowEnabled(state) || !isLoanStepRoute(state.tradeInComponent.currentUserLocationRoute)) {
            store.dispatch(updateTradeInCurrentLocation(TRADE_SUMMARY));
        }
    }

    /**
     * SCI-specific middleware function
     * Handles SCI-specific actions first, then delegates to base middleware for common trade-in logic
     */
    protected middlewareFunction = (store: Store<any>, _next: any, action: any): void => {
        const state = store.getState();

        // Handle SCI-specific actions
        switch (action.type) {
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS:
                this.handleBootstrapSuccess(store, _next, action);
                return;

            case Actions.UPDATE_TRADE_IN_CURRENT_LOCATION:
                this.handleLocationChange(store, action, state);
                return;

            case Actions.TRADE_IN_OPENED:
                store.dispatch(tradeInComponentActionCreators.fetchTradeInComponentYears(SALES_CHECKIN_APP_PREFIX));
                return;

            case CLEAR_ROLE_ASSUMPTION:
                store.dispatch({ type: Actions.RESET_TRADE_IN });
                return;

            case Actions.REMOVE_TRADE_IN:
                this.handleRemoveTradeIn(store, _next, action, state);
                return;

            case Actions.TRADE_IN_SAVE_OR_UPDATE:
                this.handleSaveOrUpdate(store, _next, action, state);
                return;

            case Actions.SUBARU_ELIGIBILITY_SUMMARY_CHECK_SUCCESS:
            case Actions.TRADE_IN_ESTIMATION_SUCCESS:
            case Actions.UPDATE_ICO_TRADE_COMPLETED:
                this.handleCompletedValuation(store, _next, action, state);
                return;

            case Actions.TRADE_IN_ADD_ICO_VALUE:
                this.handleAddIcoValue(store, state);
                return;

            case Actions.REFRESH_TRADE_IN:
                // Generic deal-update signal from the host (dispatched whenever the deal was
                // refreshed). The activity decides what to do from its own state: when the refreshed
                // deal no longer has a completed trade-in but the in-memory router is still parked on
                // the summary step, send the shopper back to the start instead of a stale summary.
                if (!store.getState().tradeInComponent?.isCompleted && tradeInHistory.location.pathname === TRADE_SUMMARY) {
                    store.dispatch(navigateTo(TRADE_VEHICLE_INFO));
                }
                return;

            default:
                // Delegate all other actions to the base trade-in middleware
                // This includes: analytics, relay messages, New Relic logging, Subaru flow, ICO flow, etc.
                this.baseTradeInMiddleware.middlewareFunction(store, _next, action);
                return;
        }
    };
}
